import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useSimpleToast, getErrorMessage } from '../components/Toast';
import { MapPin, Plus, Edit2, Trash2, X, Tag, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  primary: '#2b3380',
  error: '#dc2626'
};

function BranchConfig({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [activeTab, setActiveTab] = useState('branches');
  
  // Branches state
  const [branches, setBranches] = useState([]);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchFormData, setBranchFormData] = useState({
    name: '', address: '', latitude: '', longitude: '',
    geo_fence_radius_meters: 50, working_hours_start: '10:00',
    working_hours_end: '19:00', gst_number: '', location_type: ''
  });

  // Location Types state
  const [locationTypes, setLocationTypes] = useState([]);
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeFormData, setTypeFormData] = useState({
    name: '', description: '', icon: ''
  });

  useEffect(() => {
    fetchBranches();
    fetchLocationTypes();
  }, []);

  // ==================== BRANCHES ====================
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-config/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleBranchSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...branchFormData,
        latitude: parseFloat(branchFormData.latitude),
        longitude: parseFloat(branchFormData.longitude),
        geo_fence_radius_meters: parseInt(branchFormData.geo_fence_radius_meters)
      };

      if (editingBranch) {
        await axios.put(`${API}/hr-config/branches/${editingBranch.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Branch updated successfully!', 'success');
      } else {
        await axios.post(`${API}/hr-config/branches`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Branch created successfully!', 'success');
      }
      
      resetBranchForm();
      fetchBranches();
    } catch (error) {
      console.error('Branch save error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const resetBranchForm = () => {
    setBranchFormData({
      name: '', address: '', latitude: '', longitude: '',
      geo_fence_radius_meters: 50, working_hours_start: '10:00',
      working_hours_end: '19:00', gst_number: '', location_type: ''
    });
    setEditingBranch(null);
    setShowBranchForm(false);
  };

  const handleEditBranch = (branch) => {
    setEditingBranch(branch);
    setBranchFormData({
      name: branch.name,
      address: branch.address,
      latitude: branch.latitude.toString(),
      longitude: branch.longitude.toString(),
      geo_fence_radius_meters: branch.geo_fence_radius_meters,
      working_hours_start: branch.working_hours_start,
      working_hours_end: branch.working_hours_end,
      gst_number: branch.gst_number || '',
      location_type: branch.location_type || ''
    });
    setShowBranchForm(true);
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hr-config/branches/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Branch deleted!', 'success');
      fetchBranches();
    } catch (error) {
      console.error('Branch delete error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  // ==================== LOCATION TYPES ====================
  const fetchLocationTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/location-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocationTypes(response.data.location_types || []);
    } catch (error) {
      console.error('Failed to fetch location types:', error);
    }
  };

  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingType) {
        await axios.put(`${API}/master-data/location-types/${editingType.id}`, typeFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Location type updated!', 'success');
      } else {
        await axios.post(`${API}/master-data/location-types`, typeFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Location type created!', 'success');
      }

      resetTypeForm();
      fetchLocationTypes();
    } catch (error) {
      console.error('Location type save error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const resetTypeForm = () => {
    setTypeFormData({ name: '', description: '', icon: '' });
    setEditingType(null);
    setShowTypeForm(false);
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setTypeFormData({
      name: type.name,
      description: type.description || '',
      icon: type.icon || ''
    });
    setShowTypeForm(true);
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location type?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/master-data/location-types/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Location type deleted!', 'success');
      fetchLocationTypes();
    } catch (error) {
      console.error('Location type delete error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const getLocationTypeName = (typeId) => {
    const type = locationTypes.find(t => t.id === typeId || t.name === typeId);
    return type ? `${type.icon || ''} ${type.name}`.trim() : typeId || 'Not set';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: COLORS.primary}}>Branch & Location Management</h1>
          <p className="text-gray-600 mt-2">Manage office locations, geo-fencing, and location types</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b">
            <button
              onClick={() => { setActiveTab('branches'); setShowBranchForm(false); setShowTypeForm(false); }}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'branches' ? 'border-b-4 text-blue-900' : 'text-gray-600 hover:text-gray-900'
              }`}
              style={activeTab === 'branches' ? { borderBottomColor: COLORS.primary, color: COLORS.primary } : {}}
            >
              <MapPin className="w-4 h-4 inline mr-2" />
              Branches ({branches.length})
            </button>
            <button
              onClick={() => { setActiveTab('types'); setShowBranchForm(false); setShowTypeForm(false); }}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'types' ? 'border-b-4 text-blue-900' : 'text-gray-600 hover:text-gray-900'
              }`}
              style={activeTab === 'types' ? { borderBottomColor: COLORS.primary, color: COLORS.primary } : {}}
            >
              <Tag className="w-4 h-4 inline mr-2" />
              Location Types ({locationTypes.length})
            </button>
          </div>
        </div>

        {/* BRANCHES TAB */}
        {activeTab === 'branches' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-1 mr-4">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">📍 Geo-Fencing:</span> Employees can only mark attendance within the specified radius from branch location.
                </p>
              </div>
              <button
                onClick={() => setShowBranchForm(!showBranchForm)}
                className="px-4 py-2 rounded-lg text-white font-medium flex items-center gap-2"
                style={{backgroundColor: showBranchForm ? COLORS.error : COLORS.primary}}
              >
                {showBranchForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {showBranchForm ? 'Cancel' : 'Add Branch'}
              </button>
            </div>

            {/* Branch Form */}
            {showBranchForm && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-bold mb-4" style={{color: COLORS.primary}}>
                  {editingBranch ? 'Edit Branch' : 'Add New Branch'}
                </h2>
                <form onSubmit={handleBranchSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Branch Name *</label>
                      <input
                        type="text"
                        value={branchFormData.name}
                        onChange={(e) => setBranchFormData({...branchFormData, name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="e.g., Mumbai Office"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Location Type</label>
                      <select
                        value={branchFormData.location_type}
                        onChange={(e) => setBranchFormData({...branchFormData, location_type: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">Select Type</option>
                        {locationTypes.map(type => (
                          <option key={type.id} value={type.id}>{type.icon} {type.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Address *</label>
                      <textarea
                        value={branchFormData.address}
                        onChange={(e) => setBranchFormData({...branchFormData, address: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        rows="2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Latitude *</label>
                      <input
                        type="number"
                        step="any"
                        value={branchFormData.latitude}
                        onChange={(e) => setBranchFormData({...branchFormData, latitude: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="28.6139"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Longitude *</label>
                      <input
                        type="number"
                        step="any"
                        value={branchFormData.longitude}
                        onChange={(e) => setBranchFormData({...branchFormData, longitude: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="77.2090"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Geo-Fence Radius (m) *</label>
                      <input
                        type="number"
                        value={branchFormData.geo_fence_radius_meters}
                        onChange={(e) => setBranchFormData({...branchFormData, geo_fence_radius_meters: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">GST Number</label>
                      <input
                        type="text"
                        value={branchFormData.gst_number}
                        onChange={(e) => setBranchFormData({...branchFormData, gst_number: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="22AAAAA0000A1Z5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Working Hours Start *</label>
                      <input
                        type="time"
                        value={branchFormData.working_hours_start}
                        onChange={(e) => setBranchFormData({...branchFormData, working_hours_start: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Working Hours End *</label>
                      <input
                        type="time"
                        value={branchFormData.working_hours_end}
                        onChange={(e) => setBranchFormData({...branchFormData, working_hours_end: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button type="submit" className="px-6 py-2 rounded-lg text-white" style={{backgroundColor: COLORS.primary}}>
                      {editingBranch ? 'Update Branch' : 'Create Branch'}
                    </button>
                    <button type="button" onClick={resetBranchForm} className="px-6 py-2 rounded-lg border">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Branches List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {branches.map((branch) => (
                <div key={branch.id} className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-8 h-8" style={{color: COLORS.primary}} />
                      <div>
                        <h3 className="text-lg font-bold" style={{color: COLORS.primary}}>{branch.name}</h3>
                        <p className="text-xs text-gray-500">{getLocationTypeName(branch.location_type)}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handleEditBranch(branch)} className="p-2 hover:bg-blue-50 rounded-lg">
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </button>
                      <button onClick={() => handleDeleteBranch(branch.id)} className="p-2 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>📍 {branch.address}</p>
                    <p>🌐 {branch.latitude}, {branch.longitude}</p>
                    <p>⭕ Geo-Fence: {branch.geo_fence_radius_meters}m</p>
                    <p>🕐 {branch.working_hours_start} - {branch.working_hours_end}</p>
                    {branch.gst_number && <p>📋 GST: {branch.gst_number}</p>}
                  </div>
                </div>
              ))}
            </div>
            {branches.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No branches configured yet</p>
              </div>
            )}
          </>
        )}

        {/* LOCATION TYPES TAB */}
        {activeTab === 'types' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-1 mr-4">
                <div className="flex items-start gap-2">
                  <Settings className="w-4 h-4 text-blue-600 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">Location Types</span> categorize your branches (e.g., Head Office, Warehouse, Showroom). They appear as dropdown options when creating branches.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowTypeForm(!showTypeForm)}
                className="px-4 py-2 rounded-lg text-white font-medium flex items-center gap-2"
                style={{backgroundColor: showTypeForm ? COLORS.error : COLORS.primary}}
              >
                {showTypeForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {showTypeForm ? 'Cancel' : 'Add Type'}
              </button>
            </div>

            {/* Type Form */}
            {showTypeForm && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-bold mb-4" style={{color: COLORS.primary}}>
                  {editingType ? 'Edit Location Type' : 'Add New Location Type'}
                </h2>
                <form onSubmit={handleTypeSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Type Name *</label>
                      <input
                        type="text"
                        value={typeFormData.name}
                        onChange={(e) => setTypeFormData({...typeFormData, name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="e.g., Warehouse, Showroom"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Icon (Emoji)</label>
                      <input
                        type="text"
                        value={typeFormData.icon}
                        onChange={(e) => setTypeFormData({...typeFormData, icon: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="🏢 🏪 📦"
                        maxLength="2"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <textarea
                        value={typeFormData.description}
                        onChange={(e) => setTypeFormData({...typeFormData, description: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        rows="2"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button type="submit" className="px-6 py-2 rounded-lg text-white" style={{backgroundColor: COLORS.primary}}>
                      {editingType ? 'Update Type' : 'Create Type'}
                    </button>
                    <button type="button" onClick={resetTypeForm} className="px-6 py-2 rounded-lg border">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Location Types List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {locationTypes.map((type) => (
                <div key={type.id} className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {type.icon && <span className="text-2xl">{type.icon}</span>}
                      <h3 className="text-lg font-bold" style={{color: COLORS.primary}}>{type.name}</h3>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      type.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {type.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {type.description && <p className="text-sm text-gray-600 mb-3">{type.description}</p>}
                  <div className="flex gap-2 pt-3 border-t">
                    <button onClick={() => handleEditType(type)} className="flex-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center justify-center gap-1">
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => handleDeleteType(type.id)} className="flex-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center justify-center gap-1">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {locationTypes.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <Tag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No location types configured yet</p>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default BranchConfig;
